/**
 * The map the player is keeping.
 *
 * Not a minimap and not a floor plan: a sheet of paper with the parts of the
 * building they have actually stood in drawn onto it, badly, by hand. Opening
 * it stops the game — `UISystem.blocking` covers this panel, so reading the map
 * is a thing you stop and do rather than a readout you play through.
 *
 * Three rules keep it from solving the level:
 *  - Fog of war. A cell is on the map because they went near it or saw down a
 *    corridor at it. Everything else is blank paper. The map records where they
 *    have been; it does not show them where to go.
 *  - Only landmarks and ways out are marked. No items, no entities. Finding the
 *    building's supplies is the game, and a monster on a map is a radar.
 *  - Slides are drawn loudest of all. They are the fastest way down and the one
 *    thing a player who has found one will want to find again.
 *
 * Nothing here builds DOM after construction, and the canvas is redrawn only
 * when the fog, the level or the view actually changed — which, since the panel
 * halts the simulation, is almost never while it is up.
 */

import { Cell } from '../core/types';
import type { ExitPoint, GameContext, GeneratedLevel, Grid, PlayerState, PropKind, PropSpot } from '../core/types';
import { add, el } from './dom';

/* ------------------------------------------------------------------ *
 * Tuning
 * ------------------------------------------------------------------ */

/** Fog bits, packed one cell per byte. */
const SEEN = 1;
/** The player's own cell. Drawn a shade brighter: the route, not just the view. */
const WALKED = 2;

/** Cells revealed around the player regardless of walls — arm's length. */
const NEAR_RADIUS = 2;
/** Cells a sightline reaches down an open corridor. */
const SIGHT_CELLS = 12;
/**
 * Rays cast per cell entered. Fifty is enough that a doorway at twelve cells is
 * never missed between two rays, and the whole sweep runs only when the player
 * crosses a cell boundary — roughly once every three metres of walking.
 */
const RAY_COUNT = 50;
const RAY_STEP = 0.5;

/** Device-independent pixels per grid cell, smallest first. */
const ZOOMS: readonly number[] = [4, 6, 9, 13];
const DEFAULT_ZOOM = 2;
/** Roughly how far one pan press moves the paper, in device-independent pixels. */
const PAN_PIXELS = 96;

/**
 * The palette, mirroring the custom properties in `styles.css`. A canvas cannot
 * read those without a `getComputedStyle` per draw, and the map is the one place
 * in the UI that paints rather than styles.
 */
const INK = '#080706';
const AMBER = '#e8d98a';
const AMBER_DIM = '#a4954e';
const AMBER_FAINT = '#6f6538';
const WATER = 'rgba(159, 196, 216, 0.3)';
const FONT = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

/** Props worth drawing. Everything else is furniture, and furniture is not a map. */
const LANDMARKS: ReadonlySet<PropKind> = new Set<PropKind>(['graffiti', 'tally-marks', 'arrow', 'sign', 'camp']);

/** How an exit reads on paper. Slides are handled separately and loudly. */
const EXIT_LABEL: Record<ExitPoint['kind'], string> = {
  noclip: 'GAP',
  stairwell: 'STAIRS',
  hole: 'HOLE',
  door: 'DOOR',
  slide: 'SLIDE',
};

/** Cell-edge endpoints as offsets from the cell corner, per neighbour direction. */
const EDGE: ReadonlyArray<readonly [number, number, number, number]> = [
  [0, 0, 0, 1], // -X
  [1, 0, 1, 1], // +X
  [0, 0, 1, 0], // -Y
  [0, 1, 1, 1], // +Y
];
const NEIGHBOUR_DX = [-1, 1, 0, 0];
const NEIGHBOUR_DY = [0, 0, -1, 1];

/** The two strokes every wall gets: one committed, one gone over again. */
const WALL_PASSES: ReadonlyArray<{ salt: number; alpha: number; width: number }> = [
  { salt: 11, alpha: 0.9, width: 0.15 },
  { salt: 29, alpha: 0.34, width: 0.1 },
];

const TAU = Math.PI * 2;

/** How much of the screen the map is taking. */
export type MapMode = 'off' | 'hud' | 'full';

export class MapOverlay {
  readonly el = el('section', 'ui-screen ui-map');

  private readonly canvas = el('canvas', 'ui-map-canvas');
  private readonly c2d = this.canvas.getContext('2d');
  private readonly place = el('span', 'ui-head-note');

  /** One byte per cell, sized to the current level's grid. */
  private fog = new Uint8Array(0);
  private level: GeneratedLevel | null = null;
  /** Row-major index of the cell the player was in when the fog last grew. */
  private lastCell = -1;

  /**
   * Off, pinned to the corner of the HUD, or filling the screen.
   *
   * Only the full screen halts the simulation. The corner version is a readout
   * you play with — it has to be small enough not to take the room away, and it
   * must never block, or the player is fighting the map instead of the level.
   */
  private mode: MapMode = 'off';
  private dirty = true;
  private zoom = DEFAULT_ZOOM;
  /** Offset of the paper from the player, in cells. Zero means "follow me". */
  private panX = 0;
  private panY = 0;

  constructor() {
    const head = el('header', 'ui-head');
    this.place.textContent = '';
    add(head, el('h2', 'ui-head-title', 'MAP'), this.place);

    const frame = el('div', 'ui-map-frame');
    frame.appendChild(this.canvas);

    const hint = el('footer', 'ui-hint');
    hint.innerHTML =
      '<span><kbd>M</kbd> close</span><span><kbd>&#8592;&#8593;&#8595;&#8594;</kbd> pan</span>' +
      '<span><kbd>+</kbd> <kbd>&minus;</kbd> zoom</span><span><kbd>SPACE</kbd> centre</span>';

    add(this.el, head, frame, hint);
    this.el.hidden = true;
    window.addEventListener('resize', this.onResize);
  }

  /** Blocking. The corner map deliberately is not: you walk around with it up. */
  get open(): boolean {
    return this.mode === 'full';
  }

  /** Visible in any form, which is what decides whether it is worth redrawing. */
  get visible(): boolean {
    return this.mode !== 'off';
  }

  /* ---------------------------------------------------------------- *
   * Opening and closing
   * ---------------------------------------------------------------- */

  /** off -> corner -> full -> off. */
  cycle(): void {
    this.setMode(this.mode === 'off' ? 'hud' : this.mode === 'hud' ? 'full' : 'off');
  }

  close(): void {
    this.setMode('off');
  }

  private setMode(mode: MapMode): void {
    if (mode === this.mode) return;
    this.mode = mode;
    this.el.hidden = mode === 'off';
    this.el.classList.toggle('ui-map--hud', mode === 'hud');
    if (mode === 'off') return;
    // Always comes back centred on the player. Somebody who panned two rooms
    // away and then walked somewhere else should not have to find themselves.
    this.recentre();
    this.dirty = true;
    this.onResize();
  }

  /** New run. The paper is blank again even if the floor is the same one. */
  reset(): void {
    this.close();
    this.fog.fill(0);
    this.lastCell = -1;
    this.zoom = DEFAULT_ZOOM;
    this.recentre();
  }

  dispose(): void {
    window.removeEventListener('resize', this.onResize);
  }

  /* ---------------------------------------------------------------- *
   * Frame
   * ---------------------------------------------------------------- */

  /**
   * Called every frame, open or not: the fog is a record of the whole run and
   * has to keep growing while the player walks, which is exactly when the map
   * is put away.
   */
  update(ctx: GameContext): void {
    this.syncLevel(ctx.level);
    this.trackFog(ctx.level.grid, ctx.player);
    if (this.mode === 'off' || !this.dirty) return;
    this.dirty = false;
    this.render(ctx.level, ctx.player);
  }

  /** Pan and zoom. Returns true when the key was the map's. */
  key(e: KeyboardEvent): boolean {
    const step = Math.max(2, Math.round(PAN_PIXELS / ZOOMS[this.zoom]!));
    switch (e.code) {
      case 'ArrowLeft':
      case 'KeyA':
        return this.pan(-step, 0);
      case 'ArrowRight':
      case 'KeyD':
        return this.pan(step, 0);
      case 'ArrowUp':
      case 'KeyW':
        return this.pan(0, -step);
      case 'ArrowDown':
      case 'KeyS':
        return this.pan(0, step);
      case 'Equal':
      case 'NumpadAdd':
        return this.setZoom(this.zoom + 1);
      case 'Minus':
      case 'NumpadSubtract':
        return this.setZoom(this.zoom - 1);
      case 'Space':
      case 'Home':
        this.recentre();
        this.dirty = true;
        return true;
      default:
        return false;
    }
  }

  private recentre(): void {
    this.panX = 0;
    this.panY = 0;
  }

  private pan(dx: number, dy: number): boolean {
    this.panX += dx;
    this.panY += dy;
    this.dirty = true;
    return true;
  }

  private setZoom(next: number): boolean {
    const z = Math.max(0, Math.min(ZOOMS.length - 1, next));
    if (z === this.zoom) return true;
    this.zoom = z;
    this.dirty = true;
    return true;
  }

  private onResize = (): void => {
    this.dirty = true;
  };

  /* ---------------------------------------------------------------- *
   * Fog of war
   * ---------------------------------------------------------------- */

  /**
   * A new floor is a new sheet of paper. Detected from the level object rather
   * than from `level:loaded` because `game.ts` does not call `setLevel` on the
   * UI, and because a restart onto the same seed reuses the same level — which
   * `reset()` covers from the other side.
   */
  private syncLevel(level: GeneratedLevel): void {
    if (level === this.level) return;
    this.level = level;
    // Belt and braces: a descent cannot start while the map is halting the
    // simulation, but a map of the last floor over the new one would be a lie.
    this.close();
    const cells = level.grid.w * level.grid.h;
    this.fog = this.fog.length === cells ? this.fog.fill(0) : new Uint8Array(cells);
    this.lastCell = -1;
    this.place.textContent = level.spec.name.toUpperCase();
    this.dirty = true;
  }

  /**
   * The fog as it stands, one byte per cell, or null before any floor has been
   * drawn. A copy: the live array is written to every few metres of walking.
   */
  exportFog(): Uint8Array | null {
    return this.fog.length > 0 ? this.fog.slice() : null;
  }

  /**
   * Put a saved fog back over the current floor.
   *
   * The length check is the whole safety story. A resumed run regenerates its
   * floor from the same seed and gets the same grid, so a mismatch means the
   * generator moved under the save — and half a map of a building that no
   * longer exists is worse than a blank sheet.
   */
  importFog(fog: Uint8Array): void {
    if (this.fog.length === 0 || fog.length !== this.fog.length) return;
    this.fog.set(fog);
    // The player is standing somewhere; let the next frame's `trackFog` join
    // where they are to what they had, rather than leaving a hole at their feet.
    this.lastCell = -1;
    this.dirty = true;
  }

  /**
   * Grow the fog. Only runs when the player crosses into a new cell, so the
   * sightline sweep costs nothing per frame: a couple of thousand array writes
   * every three metres of walking.
   */
  private trackFog(grid: Grid, player: PlayerState): void {
    const cx = Math.floor(player.pos.x / grid.cellSize);
    const cy = Math.floor(player.pos.z / grid.cellSize);
    if (!grid.inBounds(cx, cy)) return;
    const here = cy * grid.w + cx;
    if (here === this.lastCell) return;
    this.lastCell = here;
    this.fog[here] = SEEN | WALKED;

    for (let dy = -NEAR_RADIUS; dy <= NEAR_RADIUS; dy++) {
      for (let dx = -NEAR_RADIUS; dx <= NEAR_RADIUS; dx++) {
        if (dx * dx + dy * dy > NEAR_RADIUS * NEAR_RADIUS + 1) continue;
        this.mark(grid, cx + dx, cy + dy);
      }
    }

    // Sightlines. A corridor the player looked down is a corridor they know
    // about, and stopping the ray on the first solid cell is what keeps the
    // room next door off the paper.
    const sx = cx + 0.5;
    const sy = cy + 0.5;
    for (let r = 0; r < RAY_COUNT; r++) {
      const a = (r / RAY_COUNT) * TAU;
      const stepX = Math.cos(a) * RAY_STEP;
      const stepY = Math.sin(a) * RAY_STEP;
      let x = sx;
      let y = sy;
      for (let n = 0; n < SIGHT_CELLS / RAY_STEP; n++) {
        x += stepX;
        y += stepY;
        const ix = Math.floor(x);
        const iy = Math.floor(y);
        if (!grid.inBounds(ix, iy)) break;
        this.mark(grid, ix, iy);
        if (grid.isSolid(ix, iy)) break;
      }
    }
    this.dirty = true;
  }

  private mark(grid: Grid, cx: number, cy: number): void {
    if (!grid.inBounds(cx, cy)) return;
    this.fog[cy * grid.w + cx]! |= SEEN;
  }

  private isSeen(grid: Grid, cx: number, cy: number): boolean {
    if (!grid.inBounds(cx, cy)) return false;
    return (this.fog[cy * grid.w + cx]! & SEEN) !== 0;
  }

  private seenAtWorld(grid: Grid, wx: number, wz: number): boolean {
    return this.isSeen(grid, Math.floor(wx / grid.cellSize), Math.floor(wz / grid.cellSize));
  }

  /* ---------------------------------------------------------------- *
   * Drawing
   * ---------------------------------------------------------------- */

  private render(level: GeneratedLevel, player: PlayerState): void {
    const c = this.c2d;
    if (!c) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = this.canvas.getBoundingClientRect();
    const W = Math.round(rect.width * dpr);
    const H = Math.round(rect.height * dpr);
    // Laid out but not measurable yet: keep the flag and try again next frame.
    if (W <= 0 || H <= 0) {
      this.dirty = true;
      return;
    }
    if (this.canvas.width !== W) this.canvas.width = W;
    if (this.canvas.height !== H) this.canvas.height = H;

    const grid = level.grid;
    const s = ZOOMS[this.zoom]! * dpr;
    const px = player.pos.x / grid.cellSize;
    const py = player.pos.z / grid.cellSize;
    // Device pixels of grid origin, so `ox + cx * s` is any cell's left edge.
    const ox = W / 2 - (px + this.panX) * s;
    const oy = H / 2 - (py + this.panY) * s;

    const x0 = Math.max(0, Math.floor(-ox / s) - 1);
    const x1 = Math.min(grid.w - 1, Math.ceil((W - ox) / s) + 1);
    const y0 = Math.max(0, Math.floor(-oy / s) - 1);
    const y1 = Math.min(grid.h - 1, Math.ceil((H - oy) / s) + 1);

    c.setTransform(1, 0, 0, 1, 0, 0);
    c.fillStyle = INK;
    c.fillRect(0, 0, W, H);
    c.lineCap = 'round';
    c.lineJoin = 'round';

    this.drawFloors(c, grid, s, ox, oy, x0, x1, y0, y1);
    this.drawWalls(c, grid, s, ox, oy, x0, x1, y0, y1);
    this.drawProps(c, level.props, grid, s, ox, oy, dpr);
    this.drawExits(c, level.exits, grid, player, s, ox, oy, dpr, W, H);
    this.drawPlayer(c, grid, player, s, ox, oy, dpr);
  }

  /** The wash: paper the player has stood on, and paper they only looked at. */
  private drawFloors(
    c: CanvasRenderingContext2D,
    grid: Grid,
    s: number,
    ox: number,
    oy: number,
    x0: number,
    x1: number,
    y0: number,
    y1: number,
  ): void {
    // A half-pixel of overlap: adjacent cells share an edge, and rounding them
    // independently leaves a grid of hairline gaps through the shading.
    for (const [bit, fill] of [
      [SEEN, 'rgba(232, 217, 138, 0.05)'],
      [WALKED, 'rgba(232, 217, 138, 0.09)'],
    ] as const) {
      c.beginPath();
      for (let cy = y0; cy <= y1; cy++) {
        for (let cx = x0; cx <= x1; cx++) {
          if ((this.fog[cy * grid.w + cx]! & bit) === 0) continue;
          if (!grid.isWalkable(cx, cy)) continue;
          c.rect(ox + cx * s, oy + cy * s, s + 0.5, s + 0.5);
        }
      }
      c.fillStyle = fill;
      c.fill();
    }

    // Below this the marks are smaller than the ink and the wash reads better
    // without them.
    if (s < 7) return;

    c.beginPath();
    for (let cy = y0; cy <= y1; cy++) {
      for (let cx = x0; cx <= x1; cx++) {
        if ((this.fog[cy * grid.w + cx]! & SEEN) === 0) continue;
        if (grid.at(cx, cy) !== Cell.Water) continue;
        const x = ox + cx * s;
        const y = oy + cy * s;
        c.moveTo(x + s * 0.2, y + s * 0.38);
        c.lineTo(x + s * 0.8, y + s * 0.38);
        c.moveTo(x + s * 0.2, y + s * 0.68);
        c.lineTo(x + s * 0.8, y + s * 0.68);
      }
    }
    c.strokeStyle = WATER;
    c.lineWidth = Math.max(1, s * 0.05);
    c.stroke();

    // Pillars. A warehouse floor is otherwise one huge empty rectangle, and the
    // pillar grid is the only thing in it anyone navigates by.
    c.beginPath();
    for (let cy = y0; cy <= y1; cy++) {
      for (let cx = x0; cx <= x1; cx++) {
        if ((this.fog[cy * grid.w + cx]! & SEEN) === 0) continue;
        if (grid.at(cx, cy) !== Cell.Pillar) continue;
        c.rect(ox + (cx + 0.34) * s, oy + (cy + 0.34) * s, s * 0.32, s * 0.32);
      }
    }
    c.fillStyle = AMBER_FAINT;
    c.fill();
  }

  /**
   * Room outlines, drawn as if by hand.
   *
   * An edge exists where a cell the player has seen meets solid building. The
   * open sides — where they simply have not been yet — are left off, so a
   * half-explored room reads as a half-drawn room rather than as a box.
   *
   * The wobble is hashed off the *lattice corner*, not the edge, so two walls
   * meeting at a corner are displaced together and the outline stays joined up.
   */
  private drawWalls(
    c: CanvasRenderingContext2D,
    grid: Grid,
    s: number,
    ox: number,
    oy: number,
    x0: number,
    x1: number,
    y0: number,
    y1: number,
  ): void {
    const amp = s * 0.15;
    for (const pass of WALL_PASSES) {
      c.beginPath();
      for (let cy = y0; cy <= y1; cy++) {
        for (let cx = x0; cx <= x1; cx++) {
          if ((this.fog[cy * grid.w + cx]! & SEEN) === 0) continue;
          if (!grid.isWalkable(cx, cy)) continue;
          for (let d = 0; d < 4; d++) {
            if (!grid.isSolid(cx + NEIGHBOUR_DX[d]!, cy + NEIGHBOUR_DY[d]!)) continue;
            const [ax, ay, bx, by] = EDGE[d]!;
            const lax = cx + ax;
            const lay = cy + ay;
            const lbx = cx + bx;
            const lby = cy + by;
            const x1p = ox + lax * s + wobble(lax, lay, pass.salt) * amp;
            const y1p = oy + lay * s + wobble(lax, lay, pass.salt + 7) * amp;
            const x2p = ox + lbx * s + wobble(lbx, lby, pass.salt) * amp;
            const y2p = oy + lby * s + wobble(lbx, lby, pass.salt + 7) * amp;
            // A bowed middle is the difference between a ruled line and a drawn
            // one. Keyed per edge so neighbouring walls bow independently.
            const mx = (x1p + x2p) / 2 + wobble(cx * 4 + d, cy, pass.salt + 31) * amp;
            const my = (y1p + y2p) / 2 + wobble(cx * 4 + d, cy, pass.salt + 53) * amp;
            c.moveTo(x1p, y1p);
            c.quadraticCurveTo(mx, my, x2p, y2p);
          }
        }
      }
      c.strokeStyle = AMBER_DIM;
      c.globalAlpha = pass.alpha;
      c.lineWidth = Math.max(1, s * pass.width);
      c.stroke();
    }
    c.globalAlpha = 1;
  }

  /** The things somebody left behind, which is what makes one room not another. */
  private drawProps(
    c: CanvasRenderingContext2D,
    props: readonly PropSpot[],
    grid: Grid,
    s: number,
    ox: number,
    oy: number,
    dpr: number,
  ): void {
    const r = Math.max(4 * dpr, s * 0.42);
    c.strokeStyle = AMBER_DIM;
    c.lineWidth = Math.max(1, 1.3 * dpr);
    c.globalAlpha = 0.85;
    for (const prop of props) {
      if (!LANDMARKS.has(prop.kind)) continue;
      if (!this.seenAtWorld(grid, prop.pos.x, prop.pos.z)) continue;
      const x = ox + (prop.pos.x / grid.cellSize) * s;
      const y = oy + (prop.pos.z / grid.cellSize) * s;
      c.beginPath();
      switch (prop.kind) {
        case 'arrow': {
          // `variant & 1` is the side it points, measured off the prop's own
          // facing: bit set means the direction 90 degrees clockwise from the
          // way it looks out of its wall. See `PropSpot.variant`.
          const side = prop.variant & 1 ? 1 : -1;
          const dx = Math.cos(prop.yaw) * side;
          const dy = Math.sin(prop.yaw) * side;
          const nx = -dy;
          const ny = dx;
          c.moveTo(x - dx * r, y - dy * r);
          c.lineTo(x + dx * r, y + dy * r);
          c.moveTo(x + dx * r * 0.2 + nx * r * 0.55, y + dy * r * 0.2 + ny * r * 0.55);
          c.lineTo(x + dx * r, y + dy * r);
          c.lineTo(x + dx * r * 0.2 - nx * r * 0.55, y + dy * r * 0.2 - ny * r * 0.55);
          break;
        }
        case 'tally-marks': {
          for (let i = 0; i < 4; i++) {
            const tx = x + (i - 1.5) * r * 0.45;
            c.moveTo(tx, y - r * 0.7);
            c.lineTo(tx, y + r * 0.7);
          }
          c.moveTo(x - r * 0.85, y + r * 0.7);
          c.lineTo(x + r * 0.85, y - r * 0.7);
          break;
        }
        case 'camp': {
          c.moveTo(x, y - r);
          c.lineTo(x + r * 0.9, y + r * 0.7);
          c.lineTo(x - r * 0.9, y + r * 0.7);
          c.closePath();
          break;
        }
        case 'sign': {
          c.rect(x - r * 0.9, y - r * 0.6, r * 1.8, r * 1.2);
          c.moveTo(x - r * 0.5, y);
          c.lineTo(x + r * 0.5, y);
          break;
        }
        default: {
          // Graffiti: a scribble, because that is all it ever is from above.
          c.moveTo(x - r, y + r * 0.5);
          c.quadraticCurveTo(x - r * 0.3, y - r, x, y + r * 0.2);
          c.quadraticCurveTo(x + r * 0.4, y + r, x + r, y - r * 0.5);
          break;
        }
      }
      c.stroke();
    }
    c.globalAlpha = 1;
  }

  /**
   * Ways down. Slides last and loudest — an eleven-year-old asked for them to be
   * easier to find, and a marker that has to be hunted for on the map has moved
   * the problem rather than solved it. One that has gone off the edge of the
   * view gets an arrow on the border with the walk to it in metres.
   */
  private drawExits(
    c: CanvasRenderingContext2D,
    exits: readonly ExitPoint[],
    grid: Grid,
    player: PlayerState,
    s: number,
    ox: number,
    oy: number,
    dpr: number,
    W: number,
    H: number,
  ): void {
    const seen: Array<{ exit: ExitPoint; x: number; y: number }> = [];
    for (const exit of exits) {
      if (!this.seenAtWorld(grid, exit.pos.x, exit.pos.z)) continue;
      seen.push({
        exit,
        x: ox + (exit.pos.x / grid.cellSize) * s,
        y: oy + (exit.pos.z / grid.cellSize) * s,
      });
    }

    for (const { exit, x, y } of seen) {
      if (exit.kind === 'slide') continue;
      const r = Math.max(5 * dpr, s * 0.6);
      c.beginPath();
      c.arc(x, y, r, 0, TAU);
      c.strokeStyle = AMBER_DIM;
      c.lineWidth = Math.max(1, 1.6 * dpr);
      c.stroke();
      c.beginPath();
      c.moveTo(x - r * 0.45, y - r * 0.2);
      c.lineTo(x, y + r * 0.4);
      c.lineTo(x + r * 0.45, y - r * 0.2);
      c.stroke();
      text(c, EXIT_LABEL[exit.kind], x, y + r + 7 * dpr, AMBER_FAINT, 8 * dpr);
    }

    for (const { exit, x, y } of seen) {
      if (exit.kind !== 'slide') continue;
      const walk = Math.hypot(exit.pos.x - player.pos.x, exit.pos.z - player.pos.z);
      // Wider margin for the full mark than for the border arrow: the mark
      // carries two lines of label under it and they have to fit on the paper.
      const room = 46 * dpr;
      if (x > room && x < W - room && y > room && y < H - room) {
        drawSlideMark(c, x, y, Math.max(13 * dpr, s * 1.4), dpr, walk);
      } else {
        drawSlideEdge(c, x, y, W, H, 30 * dpr, dpr, walk);
      }
    }
  }

  private drawPlayer(
    c: CanvasRenderingContext2D,
    grid: Grid,
    player: PlayerState,
    s: number,
    ox: number,
    oy: number,
    dpr: number,
  ): void {
    const x = ox + (player.pos.x / grid.cellSize) * s;
    const y = oy + (player.pos.z / grid.cellSize) * s;
    // yaw 0 looks down -Z, which is up the page.
    const fx = Math.sin(player.yaw);
    const fy = -Math.cos(player.yaw);
    const r = Math.max(5 * dpr, s * 0.55);

    c.beginPath();
    c.arc(x, y, r * 2.2, 0, TAU);
    c.strokeStyle = AMBER_FAINT;
    c.lineWidth = Math.max(1, dpr);
    c.stroke();

    c.beginPath();
    c.moveTo(x + fx * r * 1.5, y + fy * r * 1.5);
    c.lineTo(x - fx * r * 0.8 - fy * r, y - fy * r * 0.8 + fx * r);
    c.lineTo(x - fx * r * 0.8 + fy * r, y - fy * r * 0.8 - fx * r);
    c.closePath();
    c.fillStyle = AMBER;
    c.fill();
  }
}

/* ------------------------------------------------------------------ *
 * Painting helpers
 * ------------------------------------------------------------------ */

/** The slide itself: a filled disc with the way down punched out of it. */
function drawSlideMark(c: CanvasRenderingContext2D, x: number, y: number, r: number, dpr: number, walk: number): void {
  c.beginPath();
  c.arc(x, y, r, 0, TAU);
  c.strokeStyle = AMBER;
  c.lineWidth = Math.max(2, 2.2 * dpr);
  c.stroke();

  c.beginPath();
  c.arc(x, y, r * 0.62, 0, TAU);
  c.fillStyle = AMBER;
  c.fill();

  c.beginPath();
  c.moveTo(x, y - r * 0.34);
  c.lineTo(x, y + r * 0.3);
  c.moveTo(x - r * 0.26, y + r * 0.02);
  c.lineTo(x, y + r * 0.32);
  c.lineTo(x + r * 0.26, y + r * 0.02);
  c.strokeStyle = INK;
  c.lineWidth = Math.max(2, r * 0.16);
  c.stroke();

  text(c, 'S L I D E', x, y + r + 9 * dpr, AMBER, 10 * dpr);
  text(c, `${Math.round(walk)} M`, x, y + r + 21 * dpr, AMBER_DIM, 8 * dpr);
}

/** A slide off the edge of the paper: an arrow on the border, and how far. */
function drawSlideEdge(
  c: CanvasRenderingContext2D,
  x: number,
  y: number,
  W: number,
  H: number,
  inset: number,
  dpr: number,
  walk: number,
): void {
  const cx = W / 2;
  const cy = H / 2;
  const dx = x - cx;
  const dy = y - cy;
  const len = Math.hypot(dx, dy) || 1;
  const nx = dx / len;
  const ny = dy / len;
  // Walk out from the centre until the first border is hit.
  const t = Math.min(Math.abs(nx) > 1e-4 ? (cx - inset) / Math.abs(nx) : Infinity, Math.abs(ny) > 1e-4 ? (cy - inset) / Math.abs(ny) : Infinity);
  const ex = cx + nx * t;
  const ey = cy + ny * t;
  const r = 11 * dpr;

  c.beginPath();
  c.moveTo(ex + nx * r, ey + ny * r);
  c.lineTo(ex - nx * r * 0.5 - ny * r * 0.7, ey - ny * r * 0.5 + nx * r * 0.7);
  c.lineTo(ex - nx * r * 0.5 + ny * r * 0.7, ey - ny * r * 0.5 - nx * r * 0.7);
  c.closePath();
  c.fillStyle = AMBER;
  c.fill();

  text(c, 'S L I D E', ex - nx * r * 2.6, ey - ny * r * 2.6, AMBER, 9 * dpr);
  text(c, `${Math.round(walk)} M`, ex - nx * r * 2.6, ey - ny * r * 2.6 + 11 * dpr, AMBER_DIM, 8 * dpr);
}

function text(c: CanvasRenderingContext2D, s: string, x: number, y: number, color: string, px: number): void {
  c.font = `${Math.round(px)}px ${FONT}`;
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  c.fillStyle = color;
  c.fillText(s, x, y);
}

/**
 * Repeatable pseudo-random in -0.5..0.5 from a pair of integers.
 *
 * Hashed rather than drawn from an RNG so the same corner wobbles the same way
 * on every redraw: a map that reshuffles its own handwriting each time it is
 * opened is a screensaver.
 */
function wobble(x: number, y: number, salt: number): number {
  let h = (Math.imul(x, 374761393) + Math.imul(y, 668265263) + Math.imul(salt, 2246822519)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h & 0xffff) / 0xffff - 0.5;
}
